import { Clock, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { SearchFilters } from './use-search'

// --- Types ---

export type SearchHistoryEntry = {
  readonly id: string
  readonly filters: SearchFilters
  readonly resultCount: number
  readonly timestamp: number
}

const STORAGE_KEY = 'dca_search_history'
const MAX_HISTORY = 5

// --- Storage helpers ---

export function loadSearchHistory(): readonly SearchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed as SearchHistoryEntry[]
  } catch {
    return []
  }
}

export function saveSearchToHistory(filters: SearchFilters, resultCount: number): void {
  const hasFilters = Object.values(filters).some((v) => v && v.trim().length > 0)
  if (!hasFilters) return

  const existing = loadSearchHistory()
  const newEntry: SearchHistoryEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    filters,
    resultCount,
    timestamp: Date.now(),
  }

  const updated = [newEntry, ...existing].slice(0, MAX_HISTORY)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch {
    // localStorage quota exceeded — silently ignore
  }
}

export function clearSearchHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

// --- Formatting ---

function formatFilters(filters: SearchFilters): string {
  const parts: string[] = []
  if (filters.title) parts.push(filters.title)
  if (filters.company) parts.push(filters.company)
  if (filters.industry) parts.push(filters.industry)
  if (filters.location) parts.push(filters.location)
  if (filters.keywords) parts.push(filters.keywords)
  return parts.join(' · ') || 'All fields'
}

function timeAgo(timestamp: number): string {
  const diffMs = Date.now() - timestamp
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

// --- Component ---

type SearchHistoryProps = {
  readonly history: readonly SearchHistoryEntry[]
  readonly onContinue: (filters: SearchFilters) => void
  readonly onClear: () => void
}

export function SearchHistory({ history, onContinue, onClear }: SearchHistoryProps) {
  if (history.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Clock className="h-4 w-4 text-muted-foreground" />
            Recent Searches
          </CardTitle>
          <button
            type="button"
            onClick={onClear}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
            title="Clear search history"
          >
            <Trash2 className="h-3 w-3" />
            Clear
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="flex flex-col gap-2">
          {history.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{formatFilters(entry.filters)}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {entry.resultCount} results · {timeAgo(entry.timestamp)}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onContinue(entry.filters)}
                className="shrink-0 gap-1 text-xs"
              >
                <RotateCcw className="h-3 w-3" />
                Continue
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
